import React, { useEffect, useRef, useState, useCallback } from "react";
import FlipClassService from "../../_services/FlipClassService";
import WhiteboardCanvas, {
  WhiteboardStroke,
  WhiteboardTool,
} from "./WhiteboardCanvas";
import "./WhiteboardManager.css";

interface WhiteboardManagerProps {
  stepId: number;
  batchId: number;
  isOpen: boolean;
  onClose: () => void;
}

const PEN_COLORS = ["#1f2937", "#dc2626", "#2563eb", "#16a34a", "#eab308"];
const PEN_WIDTHS = [2, 4, 6];
const FLUSH_DEBOUNCE_MS = 250;

const WhiteboardManager: React.FC<WhiteboardManagerProps> = ({
  stepId,
  batchId,
  isOpen,
  onClose,
}) => {
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [starting, setStarting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tool, setTool] = useState<WhiteboardTool>("pen");
  const [color, setColor] = useState<string>(PEN_COLORS[0]!);
  const [width, setWidth] = useState<number>(PEN_WIDTHS[1]!);

  const pendingStrokesRef = useRef<WhiteboardStroke[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionIdRef = useRef<number | null>(null);
  const controlsRef = useRef<{
    clearAll: () => void;
    popLastStroke: () => void;
    applyRemoteStroke: (s: WhiteboardStroke) => void;
  } | null>(null);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Start session when modal opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        setStarting(true);
        setError(null);
        const res = await FlipClassService.startWhiteboard(stepId, batchId);
        if (cancelled) return;
        if (res.success && res.data) {
          setSessionId(res.data.sessionId);
        } else {
          setError("Failed to start whiteboard session");
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || "Failed to start whiteboard session");
        }
      } finally {
        if (!cancelled) setStarting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, stepId, batchId]);

  const flushPending = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    const pending = pendingStrokesRef.current;
    if (pending.length === 0) return;
    pendingStrokesRef.current = [];
    try {
      await FlipClassService.appendWhiteboardStrokes(sid, pending);
    } catch (err) {
      // Re-queue and surface; teacher's local canvas already shows the ink
      pendingStrokesRef.current = [...pending, ...pendingStrokesRef.current];
      console.warn("Whiteboard stroke flush failed:", err);
    }
  }, []);

  const handleStrokeComplete = useCallback(
    (stroke: WhiteboardStroke) => {
      pendingStrokesRef.current.push(stroke);
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(flushPending, FLUSH_DEBOUNCE_MS);
    },
    [flushPending],
  );

  const handleClear = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    controlsRef.current?.clearAll();
    try {
      await FlipClassService.sendWhiteboardControl(sid, "clear");
    } catch (err) {
      console.warn("Clear control failed:", err);
    }
  }, []);

  const handleUndo = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    controlsRef.current?.popLastStroke();
    try {
      await FlipClassService.sendWhiteboardControl(sid, "undo");
    } catch (err) {
      console.warn("Undo control failed:", err);
    }
  }, []);

  const handleClose = useCallback(async () => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    await flushPending();
    const sid = sessionIdRef.current;
    if (sid) {
      try {
        setClosing(true);
        await FlipClassService.closeWhiteboard(sid);
      } catch (err) {
        console.warn("Close whiteboard failed:", err);
      } finally {
        setClosing(false);
      }
    }
    setSessionId(null);
    onClose();
  }, [flushPending, onClose]);

  // Best-effort close on unmount if user navigates away without clicking Close
  useEffect(() => {
    return () => {
      const sid = sessionIdRef.current;
      if (sid) {
        FlipClassService.closeWhiteboard(sid).catch(() => {});
      }
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="wb-modal-overlay" onClick={handleClose}>
      <div
        className="wb-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="In-class whiteboard"
      >
        <div className="wb-toolbar">
          <div className="wb-toolbar-left">
            <div className="wb-tool-group" role="radiogroup" aria-label="Tool">
              <button
                type="button"
                className={`wb-tool-btn ${tool === "pen" ? "active" : ""}`}
                onClick={() => setTool("pen")}
                title="Pen"
              >
                <span aria-hidden>✏️</span> Pen
              </button>
              <button
                type="button"
                className={`wb-tool-btn ${tool === "highlight" ? "active" : ""}`}
                onClick={() => setTool("highlight")}
                title="Highlighter"
              >
                <span aria-hidden>🖍️</span> Highlight
              </button>
              <button
                type="button"
                className={`wb-tool-btn ${tool === "eraser" ? "active" : ""}`}
                onClick={() => setTool("eraser")}
                title="Eraser"
              >
                <span aria-hidden>🧽</span> Eraser
              </button>
            </div>

            <div className="wb-tool-group" aria-label="Color">
              {PEN_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`wb-color-swatch ${color === c ? "active" : ""}`}
                  style={{ background: c }}
                  onClick={() => setColor(c)}
                  disabled={tool === "eraser"}
                  aria-label={`Color ${c}`}
                />
              ))}
            </div>

            <div className="wb-tool-group" aria-label="Width">
              {PEN_WIDTHS.map((w) => (
                <button
                  key={w}
                  type="button"
                  className={`wb-width-btn ${width === w ? "active" : ""}`}
                  onClick={() => setWidth(w)}
                  title={`Width ${w}`}
                >
                  <span
                    className="wb-width-dot"
                    style={{ width: w * 2 + 2, height: w * 2 + 2 }}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="wb-toolbar-right">
            <button
              type="button"
              className="wb-action-btn"
              onClick={handleUndo}
              disabled={!sessionId}
              title="Undo last stroke"
            >
              ↶ Undo
            </button>
            <button
              type="button"
              className="wb-action-btn wb-action-danger"
              onClick={handleClear}
              disabled={!sessionId}
              title="Clear the board"
            >
              Clear
            </button>
            <button
              type="button"
              className="wb-close-btn"
              onClick={handleClose}
              disabled={closing}
              title="Close whiteboard"
            >
              {closing ? "Closing…" : "Close"}
            </button>
          </div>
        </div>

        <div className="wb-stage">
          {starting && <div className="wb-loading">Starting whiteboard…</div>}
          {error && <div className="wb-error">{error}</div>}
          {!starting && sessionId && (
            <WhiteboardCanvas
              tool={tool}
              color={color}
              width={width}
              onStrokeComplete={handleStrokeComplete}
              registerControls={(c) => (controlsRef.current = c)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default WhiteboardManager;
